import React, { useMemo } from "react";

import { ApolloClient, ApolloProvider, HttpLink } from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { onError } from "@apollo/client/link/error";

import { apolloAuthLink } from "utils/apolloAuthLink";
import { isDevEnv } from "utils/env";
import { useUpdateLoadingContext } from "components/Loading";

export function WithApolloClient({
  children,
  graphqlUri,
}: {
  children: React.ReactElement;
  graphqlUri: string;
}) {
  const client = useApolloClient(graphqlUri);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

function useApolloClient(graphqlUri: string) {
  const updateLoadingContext = useUpdateLoadingContext();

  return useMemo(() => {
    let link = apolloAuthLink;
    const devErrorHandler = getDevErrorHandler(updateLoadingContext);
    if (devErrorHandler) {
      link = link.concat(devErrorHandler);
    }

    link = link.concat(
      new HttpLink({
        uri: graphqlUri,
        credentials: "same-origin",
      }),
    );
    return new ApolloClient({
      link,
      cache: new InMemoryCache({
        typePolicies: {
          GraphqlQuery: {
            queryType: true,
          },
          Bloat: {
            keyFields: false,
          },
          CheckUpIssueGroup: {
            keyFields: false,
          },
          DatabasePartialList: {
            keyFields: false,
          },
          IssueNotification: {
            keyFields: false,
          },
          IssueReference: {
            keyFields: false,
          },
          LogAnalysisTempFileCreatedBucket: {
            keyFields: false,
          },
          QueryIndexCheckIndexedColumn: {
            keyFields: false,
          },
          QueryIndexCheckTable: {
            keyFields: false,
          },
          QueryTag: {
            keyFields: false,
          },
          SchemaTableAutovacuumSetting: {
            keyFields: false,
          },
          SchemaTableInlineStats: {
            keyFields: false,
          },
          SchemaTableInlineBloatStats: {
            keyFields: false,
          },
          SchemaTableBloatStats: {
            keyFields: false,
          },
          SchemaTableStats: {
            keyFields: false,
          },
          SchemaColumnStats: {
            keyFields: false,
          },
          VacuumRunLogDetails: {
            keyFields: false,
          },
          SchemaTableVacuumInfo: {
            keyFields: false,
          },
          WaitEventCount: {
            keyFields: false,
          },
          SlackNotificationConfig: {
            keyFields: false,
          },
          PagerDutyNotificationConfig: {
            keyFields: false,
          },
          DatabaseIndexWriteOverhead: {
            keyFields: false,
          },
          VacuumCount: {
            keyFields: false,
          },
          QueryParameter: {
            keyFields: false,
          },
          QueryForWorkbookLegacy: {
            keyFields: false,
          },
          HintPlanDebug: {
            keyFields: false,
          },
          DatabaseQueryExplainStats: {
            keyFields: false,
          },
        },
      }),
    });
  }, [graphqlUri, updateLoadingContext]);
}

function getDevErrorHandler(
  updateLoadingContext: ReturnType<typeof useUpdateLoadingContext>,
) {
  if (!isDevEnv) {
    return undefined;
  }

  return onError(({ operation, networkError, graphQLErrors }) => {
    // eslint-disable-next-line no-console
    console.warn("Error while executing GraphQL operation:");
    // eslint-disable-next-line no-console
    console.group(operation.operationName);
    if (networkError) {
      // eslint-disable-next-line no-console
      console.warn("Network error:", networkError);
      updateLoadingContext({ error: networkError });
    }

    if (graphQLErrors) {
      graphQLErrors.forEach((err) => {
        // eslint-disable-next-line no-console
        console.warn("GraphQL error:", err);
        updateLoadingContext({ error: err });
      });
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  });
}
